import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import {
  fetchAdmissoes,
  patchAdmissao,
  sincronizarAdmissoes,
} from './integracaoApi'
import type { AdmissaoIntegracao } from './integracaoTypes'
import { CONTENT_TABS, type ContentTabId } from './integracaoContent'

const STATUS_OPTS = [
  { id: '', label: 'Todos os status' },
  { id: 'iniciada', label: 'Iniciada' },
  { id: 'doc_pendente', label: 'Doc. pendente' },
  { id: 'doc_completa', label: 'Doc. completa' },
  { id: 'integrado', label: 'Integrado' },
  { id: 'concluida', label: 'Concluída' },
]

type MainTab = 'acompanhamento' | ContentTabId

const MAIN_TABS: Array<{ id: MainTab; label: string }> = [
  { id: 'acompanhamento', label: 'Acompanhamento' },
  ...CONTENT_TABS.map((t) => ({ id: t.id as MainTab, label: t.label })),
]

function formatDate(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR')
}

function statusTone(status?: string): 'ativo' | 'pendente' | 'inativo' {
  if (status === 'concluida' || status === 'integrado') return 'ativo'
  if (status === 'doc_pendente' || status === 'iniciada') return 'pendente'
  return 'inativo'
}

export function CentralIntegracaoPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('central-integracao')
  const qc = useQueryClient()

  const [tab, setTab] = useState<MainTab>('acompanhamento')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState<AdmissaoIntegracao | null>(null)
  const [msg, setMsg] = useState('')

  const lista = useQuery({
    queryKey: ['dp', 'admissoes', q, status],
    queryFn: () => fetchAdmissoes({ busca: q || undefined, status: status || undefined }),
  })

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['dp', 'admissoes'] })
  }

  const syncMut = useMutation({
    mutationFn: () => sincronizarAdmissoes(90),
    onSuccess: async (res) => {
      setMsg(
        res.criados > 0
          ? `${res.criados} admissão(ões) criada(s) a partir de admitidos recentes.`
          : 'Nenhum colaborador pendente sem registro de admissão.',
      )
      await invalidate()
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao sincronizar.')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: Parameters<typeof patchAdmissao>[1]
    }) => patchAdmissao(id, payload),
    onSuccess: async (dados) => {
      setSelected(dados)
      setMsg('Checklist atualizado.')
      await invalidate()
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao atualizar.')
    },
  })

  const dados = lista.data?.dados || []
  const totais = lista.data?.totais
  const content = CONTENT_TABS.find((t) => t.id === tab)

  return (
    <section className="hi-page hi-integracao-page">
      <PageHeader
        title="Integração"
        subtitle="Onboarding e checklist de novos colaboradores via API DP. Guias e biblioteca corporativa no portal."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/central-integracao')}>
              Legado
            </a>
            {editable && tab === 'acompanhamento' ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={syncMut.isPending}
                onClick={() => syncMut.mutate()}
              >
                {syncMut.isPending ? 'Sincronizando…' : 'Sincronizar admitidos (90d)'}
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      <Tabs items={MAIN_TABS} value={tab} onChange={setTab} label="Seções da integração" />

      {tab === 'acompanhamento' ? (
        <>
          <KpiGrid>
            <Kpi label="Registros" value={String(totais?.total ?? dados.length)} />
            <Kpi label="Média checklist" value={`${totais?.media_pct ?? 0}%`} />
            <Kpi
              label="Pendentes"
              value={String(
                (totais?.por_status?.iniciada || 0) + (totais?.por_status?.doc_pendente || 0),
              )}
            />
            <Kpi label="Concluídas" value={String(totais?.por_status?.concluida || 0)} />
          </KpiGrid>

          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
              setQ(search.trim())
            }}
          >
            <input
              type="search"
              placeholder="Buscar colaborador"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Busca"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Status"
            >
              {STATUS_OPTS.map((o) => (
                <option key={o.id || 'todos'} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <button type="submit" className="btn btn-secondary">
              Filtrar
            </button>
          </form>

          <div className="hi-integracao-layout">
            <Section title="Admissões em integração" flush>
              {lista.isLoading ? <Muted>Carregando…</Muted> : null}
              {lista.isError ? (
                <Alert tone="danger">
                  {lista.error instanceof ApiError
                    ? lista.error.message
                    : 'Falha ao listar admissões.'}
                </Alert>
              ) : null}
              {!lista.isLoading && dados.length === 0 ? (
                <Empty>
                  Nenhuma admissão registrada. Use “Sincronizar admitidos” para criar a partir
                  dos colaboradores dos últimos 90 dias.
                </Empty>
              ) : null}
              {dados.length > 0 ? (
                <DataTable>
                  <thead>
                    <tr>
                      <th>Colaborador</th>
                      <th>Admissão</th>
                      <th>Status</th>
                      <th>Checklist</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {dados.map((a) => (
                      <tr
                        key={a.id}
                        className={selected?.id === a.id ? 'hi-row-selected' : undefined}
                      >
                        <td>
                          <strong>{a.colaborador_nome}</strong>
                          <Muted>
                            {[a.cargo_nome, a.departamento_nome].filter(Boolean).join(' · ') ||
                              '—'}
                          </Muted>
                        </td>
                        <td>
                          <Muted>{formatDate(a.data_admissao)}</Muted>
                        </td>
                        <td>
                          <StatusBadge tone={statusTone(a.status)}>
                            {a.status_display || a.status}
                          </StatusBadge>
                        </td>
                        <td>
                          <div className="hi-pct-bar" title={`${a.pct_checklist ?? 0}%`}>
                            <span style={{ width: `${a.pct_checklist ?? 0}%` }} />
                          </div>
                          <Muted>{a.pct_checklist ?? 0}%</Muted>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setSelected(a)}
                          >
                            Checklist
                          </button>
                          <Link
                            className="btn btn-secondary btn-sm"
                            to={`/app/rh/${a.colaborador_id}`}
                          >
                            Ficha
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              ) : null}
            </Section>

            {selected ? (
              <Section
                title={`Checklist — ${selected.colaborador_nome}`}
                actions={
                  editable ? (
                    <select
                      value={selected.status}
                      aria-label="Alterar status"
                      disabled={patchMut.isPending}
                      onChange={(e) =>
                        patchMut.mutate({
                          id: selected.id,
                          payload: { status: e.target.value },
                        })
                      }
                    >
                      {STATUS_OPTS.filter((o) => o.id).map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : null
                }
              >
                <ul className="hi-checklist">
                  {Object.entries(selected.checklist || {}).map(([id, item]) => (
                    <li key={id}>
                      <label className="hi-check">
                        <input
                          type="checkbox"
                          checked={Boolean(item.ok)}
                          disabled={!editable || patchMut.isPending}
                          onChange={(e) =>
                            patchMut.mutate({
                              id: selected.id,
                              payload: {
                                checklist_item: { id, ok: e.target.checked },
                              },
                            })
                          }
                        />
                        <span>{item.label || id}</span>
                      </label>
                      {item.owner ? <Muted>{item.owner}</Muted> : null}
                    </li>
                  ))}
                </ul>

                <div className="hi-integracao-flags">
                  {(
                    [
                      ['contratos_gerados', 'Contratos gerados'],
                      ['acesso_sistemas', 'Acesso aos sistemas'],
                      ['exame_admissional', 'Exame admissional'],
                      ['epi_entregue', 'EPI entregue'],
                      ['esocial_enviado', 'eSocial enviado'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="hi-check">
                      <input
                        type="checkbox"
                        checked={Boolean(selected[key])}
                        disabled={!editable || patchMut.isPending}
                        onChange={(e) =>
                          patchMut.mutate({
                            id: selected.id,
                            payload: { [key]: e.target.checked },
                          })
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </Section>
            ) : null}
          </div>
        </>
      ) : content ? (
        <Section title={content.title}>
          <div className="hi-integracao-content">{content.render()}</div>
        </Section>
      ) : null}
    </section>
  )
}
